import React, { useEffect, useMemo, useState } from 'react';
import { mlModelAPI } from '../../api';
import { EmptyState, ErrorState } from './UIComponents';
import LoadingSpinner from '../LoadingSpinner';
import { ScoreCell, SortableTableHeader } from './SpecificityInterpretation';

const DIMENSION_COLUMNS = [
  { key: 'biology', label: 'Biology' },
  { key: 'genetics', label: 'Genetics' },
  { key: 'senescence', label: 'Senescence' },
  { key: 'cell_type', label: 'Cell type' },
  { key: 'druggability', label: 'Druggability' },
  { key: 'safety', label: 'Safety' },
  { key: 'novelty', label: 'Novelty' },
];

const SORTABLE_COLUMNS = new Set([
  'rank',
  'composite_score',
  'bayesian_success_score',
  'model_importance',
  ...DIMENSION_COLUMNS.map((col) => col.key),
]);

function formatScore(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(2);
}

function DimensionDetail({ dimension }) {
  if (!dimension) return <span style={{ color: '#888' }}>—</span>;
  return (
    <div style={{ fontSize: 13, lineHeight: 1.45 }}>
      <div style={{ marginBottom: 6 }}>
        <strong>Score:</strong> {formatScore(dimension.score)}
      </div>
      <div>{dimension.interpretation}</div>
    </div>
  );
}

export function TargetScorecardSummaryBanner({ summary }) {
  if (!summary) return null;
  return (
    <div
      style={{
        marginTop: 12,
        marginBottom: 12,
        padding: '12px 14px',
        borderRadius: 8,
        border: '1px solid #cfe0f5',
        backgroundColor: '#f0f6ff',
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Target prioritization summary</div>
      <div>{summary.interpretation}</div>
      <div style={{ marginTop: 6, color: '#444' }}>
        Mean composite: {formatScore(summary.mean_composite_score)}
        {summary.mean_bayesian_success_score != null && (
          <> · Mean Bayesian: {formatScore(summary.mean_bayesian_success_score)}</>
        )}
      </div>
    </div>
  );
}

export default function TargetScorecardTable({ datasetName, modelType, featureImportance }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('bayesian_success_score');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedGene, setExpandedGene] = useState(null);

  const loadScorecards = async () => {
    if (!datasetName || !modelType) return;
    setLoading(true);
    setError(null);
    try {
      let data = await mlModelAPI.getTargetScorecards(datasetName, modelType);
      if (data?.error && Array.isArray(featureImportance) && featureImportance.length) {
        const genes = featureImportance
          .map((row) => row.feature || row.name)
          .filter((g) => g && !String(g).includes(' '))
          .slice(0, 50);
        const importance = {};
        featureImportance.slice(0, 50).forEach((row) => {
          const gene = row.feature || row.name;
          if (gene) importance[gene] = row.importance;
        });
        data = await mlModelAPI.scoreTargets(genes, importance);
      }
      if (data?.error) throw new Error(data.error);
      setPayload(data);
    } catch (err) {
      setError(err.message || 'Failed to load target scorecards.');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (datasetName && modelType) {
      loadScorecards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetName, modelType]);

  const sortedCards = useMemo(() => {
    const cards = [...(payload?.scorecards || [])];
    cards.sort((a, b) => {
      let left;
      let right;
      if (sortBy === 'rank') {
        left = cards.indexOf(a);
        right = cards.indexOf(b);
      } else if (sortBy === 'composite_score' || sortBy === 'bayesian_success_score' || sortBy === 'model_importance') {
        if (sortBy === 'model_importance') {
          left = a.model_importance;
          right = b.model_importance;
        } else {
          left = (a.integrated || {})[sortBy];
          right = (b.integrated || {})[sortBy];
        }
      } else {
        left = a.dimensions?.[sortBy]?.score;
        right = b.dimensions?.[sortBy]?.score;
      }
      const leftNum = typeof left === 'number' && Number.isFinite(left) ? left : null;
      const rightNum = typeof right === 'number' && Number.isFinite(right) ? right : null;
      if (leftNum === null && rightNum === null) return 0;
      if (leftNum === null) return 1;
      if (rightNum === null) return -1;
      return sortOrder === 'asc' ? leftNum - rightNum : rightNum - leftNum;
    });
    return cards;
  }, [payload, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (!SORTABLE_COLUMNS.has(column)) return;
    if (sortBy === column) {
      setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  if (!datasetName || !modelType) return null;

  return (
    <div style={{ marginTop: 24, padding: 24, border: '1px solid var(--border, #ddd)', borderRadius: 8, backgroundColor: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0 }}>Target Prioritization Scorecards</h3>
          <p style={{ margin: '6px 0 0', color: '#555', fontSize: 14 }}>
            Phase 2 unified ranking: Bayesian success score plus biology, druggability, safety, senescence, and cell-type dimensions.
          </p>
        </div>
        <button
          type="button"
          onClick={loadScorecards}
          disabled={loading}
          style={{
            padding: '8px 14px',
            backgroundColor: loading ? '#ccc' : '#0056b3',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Loading...' : 'Refresh scorecards'}
        </button>
      </div>

      {loading && (
        <div style={{ marginTop: 16 }}>
          <LoadingSpinner text="Building target scorecards..." />
        </div>
      )}

      {error && (
        <div style={{ marginTop: 16 }}>
          <ErrorState message={error} onRetry={loadScorecards} />
        </div>
      )}

      {!loading && !error && payload?.scorecards?.length > 0 && (
        <>
          <TargetScorecardSummaryBanner summary={payload.summary} />
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px 6px' }}>#</th>
                  <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px 6px' }}>Target</th>
                  <SortableTableHeader label="Bayesian" column="bayesian_success_score" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                    Bayesian
                  </SortableTableHeader>
                  <SortableTableHeader label="Composite" column="composite_score" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                    Composite
                  </SortableTableHeader>
                  <SortableTableHeader label="Importance" column="model_importance" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                    Importance
                  </SortableTableHeader>
                  {DIMENSION_COLUMNS.map((col) => (
                    <SortableTableHeader key={col.key} label={col.label} column={col.key} sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                      {col.label}
                    </SortableTableHeader>
                  ))}
                  <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px 6px' }}>Modality</th>
                  <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px 6px' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {sortedCards.map((card, idx) => {
                  const integrated = card.integrated || {};
                  const isExpanded = expandedGene === card.gene;
                  return (
                    <React.Fragment key={card.gene}>
                      <tr>
                        <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>{idx + 1}</td>
                        <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px', fontWeight: 600 }}>{card.gene}</td>
                        <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                          <ScoreCell score={integrated.bayesian_success_score} />
                        </td>
                        <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                          <ScoreCell score={integrated.composite_score} />
                        </td>
                        <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                          {formatScore(card.model_importance)}
                        </td>
                        {DIMENSION_COLUMNS.map((col) => (
                          <td key={col.key} style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                            <ScoreCell score={card.dimensions?.[col.key]?.score} />
                          </td>
                        ))}
                        <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                          {card.modality?.recommended || '—'}
                        </td>
                        <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                          <button
                            type="button"
                            onClick={() => setExpandedGene(isExpanded ? null : card.gene)}
                            style={{ background: 'none', border: 'none', color: '#0056b3', cursor: 'pointer', padding: 0 }}
                          >
                            {isExpanded ? 'Hide' : 'Show'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={6 + DIMENSION_COLUMNS.length} style={{ borderBottom: '1px solid #eee', padding: '12px 16px', backgroundColor: '#f8fafc' }}>
                            {integrated.bayesian_interpretation && (
                              <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
                                <strong>Bayesian integration:</strong> {integrated.bayesian_interpretation}
                              </p>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                              {DIMENSION_COLUMNS.map((col) => (
                                <div key={col.key} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, backgroundColor: '#fff' }}>
                                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{col.label}</div>
                                  <DimensionDetail dimension={card.dimensions?.[col.key]} />
                                </div>
                              ))}
                              <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, backgroundColor: '#fff' }}>
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>Modality</div>
                                <div style={{ fontSize: 13 }}>
                                  <div><strong>Recommended:</strong> {card.modality?.recommended || '—'}</div>
                                  {Array.isArray(card.modality?.rationale) && card.modality.rationale.length > 0 && (
                                    <div style={{ marginTop: 6 }}>{card.modality.rationale.join('; ')}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !error && (!payload?.scorecards || payload.scorecards.length === 0) && (
        <div style={{ marginTop: 16 }}>
          <EmptyState message="No scorecards available yet. Train a model or refresh after feature importance loads." icon="🎯" />
        </div>
      )}
    </div>
  );
}
